import { FormInputGroup } from 'framework/ui/layout';
import { Box, Grid2 as Grid } from '@mui/material';
import {
  DateInput,
  TextArea,
  FormFieldsData,
  useFormContext,
  Checkbox,
  NumberInput,
  DropdownInputOnlyBoolean,
} from 'framework/form';
import { EmployeeInput, buildEmployeeAutoSuggestLabel } from 'domain/shared/employee';
import { OutlinedCalendarIcon } from 'framework/ui/mui-builder';
import { Button } from 'framework/ui/mui-builder';
import { TeamSlotDropdown } from 'domain/shared/newProject';
import { PRIVATE_MEMBERSHIP_ROLES } from '../../core/constants';

type BackupReplacementFormProps = {
  teamStructure: any;
  isUpdateMode: boolean;
  backupReplacementLabel: string;
  onClose: () => void;
  onDelete?: (data: FormFieldsData) => Promise<void>;
};

const BackupReplacementForm = ({
  teamStructure,
  isUpdateMode,
  backupReplacementLabel,
  onDelete = () => Promise.resolve(),
  onClose,
}: BackupReplacementFormProps) => {
  const { submitForm, isSubmitEnabled, data = {} } = useFormContext();

  return (
    <Box>
      <Grid container spacing={1} pb={2}>
        <Grid size={6}>
          <EmployeeInput
            source="fromEmployeeAlias"
            label="Worker"
            isInitialNameLoad
            disabled={isUpdateMode}
            mapResults={employee => {
              const existingTeamMember = teamStructure?.members?.find(
                (member: any) => member.employeeAlias === employee.alias,
              );
              if (!existingTeamMember) {
                return null;
              }

              return {
                id: employee.alias,
                text: buildEmployeeAutoSuggestLabel(employee),
                slotId: existingTeamMember.slotId,
                roleOnProject: existingTeamMember.role,
                isPrivate: PRIVATE_MEMBERSHIP_ROLES.includes(existingTeamMember.role),
              };
            }}
          />
        </Grid>
        <Grid size={6}>
          <EmployeeInput source="toEmployeeAlias" label={backupReplacementLabel} isInitialNameLoad />
        </Grid>
        <Grid size={6}>
          <DateInput
            label="From"
            dateIcon={
              <OutlinedCalendarIcon
                sx={{
                  marginRight: '10px',
                  fontSize: '1.2rem',
                  position: 'absolute',
                  zIndex: 1,
                  left: '10px',
                  top: '10px',
                  opacity: '0.8',
                }}
              />
            }
            source="plannedStart"
          />
        </Grid>
        <Grid size={6}>
          <DateInput
            label="To"
            dateIcon={
              <OutlinedCalendarIcon
                sx={{
                  marginRight: '10px',
                  fontSize: '1.2rem',
                  position: 'absolute',
                  zIndex: 1,
                  left: '10px',
                  top: '10px',
                  opacity: '0.8',
                }}
              />
            }
            source="plannedEnd"
          />
        </Grid>
        <Grid size={12} sx={{ '& .floating-label-input': { mb: 0 } }}>
          <TextArea source="comment" label="Note" rows={3} />
        </Grid>
        <Grid size={12} sx={{ '& .MuiFormControl-root': { mb: 0 } }}>
          <Checkbox source="isPrivate" label="Invisible to the worker" />
        </Grid>
        <FormInputGroup isVisible={formData => formData.toEmployeeAlias}>
          <Grid size={6}>
            <NumberInput source="minPercent" integerOnly />
          </Grid>
          <Grid size={6}>
            <NumberInput source="maxPercent" integerOnly />
          </Grid>
          <Grid size={6}>
            <DropdownInputOnlyBoolean placeholder="Bench" source="isBench" label="Bench" />
          </Grid>
          <Grid size={12} sx={{ '& .long-form-section': { p: 0 } }}>
            <TeamSlotDropdown disabled source="slotId" projectIdSource="projectId" label="Slot" team={teamStructure} />
          </Grid>
        </FormInputGroup>
      </Grid>
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3 }}>
        {onDelete && isUpdateMode && (
          <Button variant={'contained'} color={'error'} onClick={() => onDelete(data).then(onClose)}>
            Delete
          </Button>
        )}
        <Button variant="outlined" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="contained" onClick={submitForm} disabled={!isSubmitEnabled}>
          {isUpdateMode ? 'Update' : 'Add'}
        </Button>
      </Box>
    </Box>
  );
};
export default BackupReplacementForm;
